import React, { useMemo, useState } from "react";
import {
  ScatterChart,
  Scatter,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
  Legend
} from "recharts";
import _ from "lodash";
import styles from "./style.module.scss";
import { CHART_COLORS, getRandomHexColor } from "../../../../../services/colorUtil";
import { formatAxisTick, formatChartNumber } from "../chartNumberFormat";


interface SeriesData {
  label: string;
  x: (string | number)[];
  y: number[];
}

interface ChartArtifacts {
  meta: {
    xlabel: string;
    ylabel: string;
    title?: string;
  };
  series: SeriesData[];
}

interface ScatterChartViewProps {
  artifacts: ChartArtifacts;
}

const chartStyles = {
  contentStyle: {
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    fontSize: "14px",
    fontWeight: 500,
  },
  labelStyle: { color: "#374151", fontWeight: 600 },
};

const ScatterChartView: React.FC<ScatterChartViewProps> = ({ artifacts }) => {
  const [activeSeries, setActiveSeries] = useState<string | null>(null);

  const prepared = useMemo(() => {
    const seriesArray = _.get(artifacts, "series", []) as SeriesData[];
    if (_.isEmpty(seriesArray)) return null;

    const meta = _.get(artifacts, "meta", {}) as ChartArtifacts["meta"];
    const { xlabel, ylabel } = meta;

    const allXValues = seriesArray.flatMap((series) =>
      series.x.map((xVal) => Number(xVal))
    );
    const xMin = Math.min(...allXValues);
    const xMax = Math.max(...allXValues);
    const xDomain = [xMin - 1, xMax + 1];

    return { xlabel, ylabel, seriesArray, xDomain };
  }, [artifacts]);

  if (!prepared) {
    return <div className={styles.noDataMessage}>No chart data available</div>;
  }

  const { xlabel, ylabel, seriesArray, xDomain } = prepared;

  const calculateOpacity = (label: string) => {
    if (activeSeries === null) return 1;
    return activeSeries === label ? 1 : 0.15;
  };

  const handleLegendClick = (label: string) => {
    if (activeSeries === label) {
      setActiveSeries(null);
    } else {
      setActiveSeries(label);
    }
  };

  const tooltipFormatter = (value: number, name: string) => {
    if (activeSeries !== null && activeSeries !== name) return null;

    return [formatChartNumber(Number(value), "fixed2"), name];
  };

  return (
    <div className={styles.chartContainer}>
      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart
          margin={{
            top: 30,
            right: 40,
            left: 30,
            bottom: 70,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.6} />

          <XAxis type="number" dataKey="x" name={xlabel} domain={xDomain}>
            <Label
              value={xlabel}
              offset={-15}
              position="insideBottom"
              className={styles.label}
            />
          </XAxis>

          <YAxis type="number" dataKey="y" name={ylabel} tickFormatter={formatAxisTick}>
            <Label
              value={ylabel}
              angle={-90}
              position="left"
              offset={8}
              className={styles.label}
            />
          </YAxis>

          <Tooltip
            cursor={{ strokeDasharray: "3 3" }}
            contentStyle={chartStyles.contentStyle}
            labelStyle={chartStyles.labelStyle}
            formatter={(value: number, name: string) => tooltipFormatter(value, name)}
          />

          <Legend
            verticalAlign="top"
            content={(data) => (
              <LegendView
                data={data}
                activeSeries={activeSeries}
                onLegendClick={handleLegendClick}
              />
            )}
            wrapperStyle={{ fontSize: "13px" }}
          />

          {seriesArray.map((series, i) => {
            const scatterData = series.x.map((xVal, idx) => ({
              x: Number(xVal),
              y: series.y[idx],
            }));

            const color =
              i < CHART_COLORS.length ? CHART_COLORS[i] : getRandomHexColor();

            return (
              <Scatter
                key={series.label}
                name={series.label}
                data={scatterData}
                fill={color}
                opacity={calculateOpacity(series.label)}
                shape="circle"
              />
            );
          })}
          <text x="100%" y="95%" textAnchor="end" dominantBaseline="auto" className={styles.disclaimer}>* AI Generated Graph</text>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
};

const LegendView: React.FC<any> = ({ data, activeSeries, onLegendClick }) => {
  const { payload } = data;

  return (
    <ul className={styles.legendContainer}>
      {payload.map((entry: any, index: number) => {
        const isActive = activeSeries === null || activeSeries === entry.value;

        return (
          <li
            key={`item-${index}`}
            className={styles.legendList}
            style={{
              color: isActive ? entry.color : "#cbd5e1",
              cursor: "pointer",
            }}
          >
            <span
              className={styles.legendName}
              style={{
                backgroundColor: isActive ? entry.color : "#cbd5e1",
              }}
            />
            {entry.value}
          </li>
        );
      })}
    </ul>
  );
};


export default ScatterChartView;
